import lruMap from 'lru_map';
import * as Y from 'yjs';

import { getYMutationClock } from './getYMutationClock';
import { isYValue } from './isYValue';
import { type JSONToY, jsonToY, updateYArrayWithJSON } from './jsonToY';

/*
 * Data mutation utilities that can work both on Y.JS objects and JSON objects.
 */

export type MutableData<T> = T | JSONToY<T>;

type ExtractObj<S extends object, K> = K extends keyof S ? S[K] : never;

export type PropertiesPath<S extends object, T extends readonly unknown[]> = T extends readonly [
    infer T0,
    ...infer TR,
]
    ? TR extends []
        ? ExtractObj<S, T0> extends never
            ? readonly []
            : readonly [T0]
        : ExtractObj<S, T0> extends object
          ? readonly [T0, ...PropertiesPath<ExtractObj<S, T0>, TR>]
          : ExtractObj<S, T0> extends never
            ? readonly []
            : readonly [T0]
    : readonly [];

export type ValueOfPropsPath<S extends object, T extends readonly unknown[]> = T extends readonly [
    infer T0,
    ...infer TR,
]
    ? TR extends []
        ? ExtractObj<S, T0>
        : ExtractObj<S, T0> extends object
          ? ValueOfPropsPath<ExtractObj<S, T0>, TR>
          : never
    : T extends readonly []
      ? S
      : never;

type ParentPath<T> = T extends readonly [any, ...infer TR] ? TR : [];

/**
 * Set a value deep in a JSON or Y object.
 */
export function setIn<S extends object, T extends readonly unknown[]>(
    obj: MutableData<S>,
    path: T extends PropertiesPath<S, T> ? T : never,
    value: MutableData<ValueOfPropsPath<S, T>>
) {
    if (path.length === 0) {
        return;
    }

    return mutateIn(
        obj,
        // @ts-ignore
        path.slice(0, -1) as ParentPath<T>,
        (parent) => {
            // @ts-ignore
            set(parent, path[path.length - 1], value);
        }
    );
}

/**
 * Replace values deep in a JSON or Y object.
 */
export function updateIn<S extends object, T extends readonly unknown[]>(
    obj: MutableData<S>,
    path: T extends PropertiesPath<S, T> ? T : never,
    update: Partial<ValueOfPropsPath<S, T>>
) {
    return mutateIn(obj, path, (parent) => {
        for (const [key, value] of entries(update)) {
            // @ts-ignore
            set(parent, key, value);
        }
    });
}

/**
 * Replace values in an array, mutating the array in the process.
 */
export function updateInArray<V>(arr: MutableData<V[]>, json: V[]) {
    if (arr instanceof Y.Array) {
        updateYArrayWithJSON(arr, json);
        return;
    }

    for (let i = 0; i < json.length; i++) {
        arr[i] = json[i];
    }

    if (arr.length > json.length) {
        arr.splice(json.length);
    }
}

/**
 * Set a value in a JSON or Y object.
 */
export function set<S extends object, K extends keyof S>(
    obj: MutableData<S>,
    key: K,
    value: MutableData<S[K]>
) {
    if (obj instanceof Y.Map) {
        // @ts-ignore
        if (obj.get(key) === value) {
            // Yjs throws an error when setting the same value if it's a Yjs data type.
            return;
        }

        // @ts-ignore
        obj.set(key, jsonToY(value));
    } else {
        // @ts-ignore
        obj[key] = value;
    }
}

/**
 * Get a value by its key in a JSON or Y object.
 */
export function get<S extends object, K extends keyof S>(
    obj: MutableData<S>,
    key: K
): MutableData<S[K]> {
    if (obj instanceof Y.Map || obj instanceof Y.Array) {
        // @ts-ignore
        return obj.get(key);
    }
    // @ts-ignore
    return obj[key];
}

/**
 * Get a value deep in a JSON or Y object.
 */
export function getIn<S extends object, T extends readonly unknown[]>(
    obj: MutableData<S>,
    path: T extends PropertiesPath<S, T> ? T : never
): MutableData<ValueOfPropsPath<S, T>> | undefined {
    return mutateIn(obj, path, (value) => value);
}

/**
 * Run a function on a value deep in a JSON or Y object.
 */
function mutateIn<S extends object, T extends readonly unknown[], Result>(
    obj: MutableData<S>,
    path: T extends PropertiesPath<S, T> ? T : never,
    fn: (value: ValueOfPropsPath<S, T>) => Result
): Result | undefined {
    if (path.length === 0) {
        // @ts-ignore
        return fn(obj);
    }

    if (path.length === 1) {
        // @ts-ignore
        return fn(get(obj, path[0]));
    }

    const [key, ...rest] = path;

    const next = get(obj, key);
    if (next === undefined) {
        return;
    }

    // @ts-ignore
    return mutateIn(next, rest, fn);
}

/**
 * Insert values in an array.
 */
export function insert<T>(array: MutableData<T[]>, index: number, values: T[]) {
    if (array instanceof Y.Array) {
        array.insert(
            index,
            values.map((value) => jsonToY(value))
        );
    } else {
        array.splice(index, 0, ...values);
    }
}

/**
 * Push values to an array.
 */
export function push<T>(array: MutableData<T[]>, values: T[]) {
    if (array instanceof Y.Array) {
        array.push(values.map((value) => jsonToY(value)));
    } else {
        array.push(...values);
    }
}

/**
 * Check if an array contains a value.
 */
export function includes<T>(array: MutableData<T[]>, value: T) {
    if (array instanceof Y.Array) {
        for (const item of array) {
            if (item === value) {
                return true;
            }
        }

        return false;
    }
    return array.includes(value);
}

/**
 * Check if an object contains a key.
 */
export function has<S extends object>(obj: MutableData<S>, key: string) {
    if (obj instanceof Y.Map) {
        return obj.has(key);
    }
    return key in obj;
}

/**
 * Returns the index of the first element in the array of JSON or Y object
 * where predicate is true, and -1 otherwise.
 */
export function findIndex<T>(
    array: MutableData<T[]>,
    predicate: (value: MutableData<T>) => boolean
): number {
    let index = 0;
    for (const item of array) {
        if (predicate(item)) {
            return index;
        }
        index++;
    }

    return -1;
}

/**
 * Iterate through entries in a JSON or Y object.
 */
export function* entries<S extends object>(
    obj: MutableData<S>
): Generator<[keyof S | number, MutableData<S[keyof S]>]> {
    if (obj instanceof Y.Map) {
        for (const [key, value] of obj.entries()) {
            yield [key as keyof S, value];
        }
    } else if (obj instanceof Y.Array) {
        // create a seperate generator for Y.Array as
        // for...in will also yield the custom props of the
        // Y.Array object. Also using a for...of instead of just returning the
        // Y.Array iterator as it is value generator and we need
        // to yield [index, value] for each entries.
        let index = 0;
        for (const value of obj) {
            yield [index++, value];
        }
    } else {
        for (const key in obj) {
            // @ts-ignore
            yield [key, obj[key]];
        }
    }
}

/**
 * Map values in a JSON or Y object.
 */
export function map<S extends object, T>(
    obj: MutableData<S>,
    fn: (value: MutableData<S[keyof S]>, key: keyof S) => T
): T[] {
    if (obj instanceof Y.Map) {
        return Array.from(obj.entries()).map(([key, value]) => fn(value, key as keyof S));
    }
    if (obj instanceof Y.Array) {
        return obj.map((value, index) => fn(value, index as keyof S));
    }
    return Object.entries(obj).map(([key, value]) => fn(value, key as keyof S));
}

/**
 * Delete a value in a JSON or Y object (map).
 */
export function del<S extends object, K extends keyof S>(obj: MutableData<S>, key: K) {
    if (obj instanceof Y.Map) {
        // @ts-ignore
        obj.delete(key);
    } else {
        // @ts-ignore
        delete obj[key];
    }
}

/**
 * Del a value deep in a JSON or Y object.
 */
export function delIn<S extends object, T extends readonly unknown[]>(
    obj: MutableData<S>,
    path: T extends PropertiesPath<S, T> ? T : never
) {
    if (path.length === 0) {
        return;
    }

    if (path.length === 1) {
        del(obj, path[0]);
        return;
    }

    const [key, ...rest] = path;
    const next = get(obj, key);
    if (!next) {
        return;
    }

    // @ts-ignore
    delIn(next, rest);
}

/**
 * Delete a value by its index in a JSON or Y object (array).
 */
export function delArrayIndex<T>(obj: MutableData<T[]>, index: number, length: number) {
    if (obj instanceof Y.Array) {
        obj.delete(index, length);
    } else {
        obj.splice(index, length);
    }
}

/**
 * Delete all items in a JSON or Y object (array), that are equal to `value`.
 */
export function delArrayValue<T>(obj: MutableData<T[]>, value: T) {
    for (const [index, item] of entries(obj)) {
        if (item === value) {
            delArrayIndex(obj, index as number, 1);
        }
    }
}

/**
 * Get the keys of a JSON or Y object (map).
 */
export function keys<S extends object>(obj: MutableData<S>): string[] {
    if (obj instanceof Y.Map) {
        return [...obj.keys()];
    }
    return Object.keys(obj);
}

/**
 * Get the values of a JSON or Y object (map).
 */
export function values<R>(obj: MutableData<R[]>): MutableData<R>[];
export function values<S extends object>(obj: MutableData<S>): MutableData<S[keyof S]>[];
export function values<S extends object>(obj: MutableData<S>): MutableData<S[keyof S]>[] {
    if (obj instanceof Y.Map) {
        return [...obj.values()];
    }
    if (obj instanceof Y.Array) {
        return obj.toArray();
    }
    return Object.values(obj);
}

/**
 * Memoize an operation on mutable data.
 */
export function memo<S extends object, Result>(): (
    obj: MutableData<S>,
    keys: Array<string | number>,
    transform: () => Result
) => Result {
    const memoPlainCache = new WeakMap<object, any>();
    const memoYCache = new WeakMap<object, lruMap.LRUMap<number, any>>();

    const getCache = (obj: MutableData<S>) => {
        if (isYValue(obj)) {
            const mutationsCache = memoYCache.get(obj) || new lruMap.LRUMap(50);
            memoYCache.set(obj, mutationsCache);

            const clock = getYMutationClock(obj);
            if (clock === undefined) {
                return undefined;
            }

            return mutationsCache.get(clock);
        }
        const cache = memoPlainCache.get(obj);
        return cache;
    };

    return (obj, keys, transform) => {
        let cache = getCache(obj);

        if (cache) {
            let inCache = cache;
            for (let index = 0; index < keys.length; index++) {
                const key = keys[index];
                inCache = inCache.get(key);

                if (inCache === undefined) {
                    break;
                }

                if (index === keys.length - 1) {
                    return inCache;
                }
            }
        }

        const result = transform();
        if (!cache) {
            cache = new Map();

            if (isYValue(obj)) {
                const clock = getYMutationClock(obj);
                if (clock !== undefined) {
                    memoYCache.get(obj)!.set(clock, cache);
                }
            } else {
                memoPlainCache.set(obj, cache);
            }
        }

        let inCache = cache;
        for (let index = 0; index < keys.length; index++) {
            const key = keys[index];
            const next = index === keys.length - 1 ? result : new Map();
            inCache.set(key, next);
            inCache = next;
        }

        return result;
    };
}
